import { useState } from "react";
import usePostAnalysis from "./usePostAnalysis";
import Cookies from "js-cookie";

const useProcessFilesBackend = (
  cvFile,
  mLetterfile,
  mLetterOption,
  mLetterText,
  linkedInData,
  linkedInURL,
  preferencesSummary,
  reflectionSummary,
  setBigFiveData,
  setDiscData,
  setRiasecData,
  setMbtiData,
  careerPreferences,
  reflection
) => {
  const { postAnalysisToAPI } = usePostAnalysis();

  const [analyzing, setAnalyzing] = useState(true);
  const [progress, setProgress] = useState(0);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [error, setError] = useState(null);

  const toArray = (value) => (Array.isArray(value) ? value : []);

  // Ensure a value is a finite number, defaulting to 0 if not
  const toFiniteNumber = (value, defaultValue = 0) => {
    const num = typeof value === 'number' ? value : Number(value);
    return isFinite(num) ? num : defaultValue;
  };

  const normalizeLinkedInData = (data) => {
    const safeData = data || {};
    const experience = toArray(safeData.experience).map((exp) => ({
      title: exp?.title || "",
      company: exp?.company || "",
      start_date: exp?.start_date || "",
      end_date: exp?.end_date || "",
    }));
    const skills = toArray(safeData.skills);
    const languages = toArray(safeData.languages).map((lang) => ({
      name: lang?.name || "",
      proficiency: lang?.proficiency || "",
    }));

    return {
      firstName: safeData.firstName || "",
      lastName: safeData.lastName || "",
      headline: safeData.headline || "",
      industryName: safeData.industryName || "",
      locationName: safeData.locationName || "",
      experience,
      skills,
      languages,
      displayPictureUrl: safeData.displayPictureUrl || "",
      img_800_800: safeData.img_800_800 || null,
      img_400_400: safeData.img_400_400 || null,
      img_200_200: safeData.img_200_200 || null,
      img_100_100: safeData.img_100_100 || null,
    };
  };

  const buildFallbackCvFile = (
    linkedInDataSnapshot,
    linkedInUrlToSend,
    preferencesSummaryText,
    reflectionSummaryText,
    questionnaireData
  ) => {
    const {
      firstName = "",
      lastName = "",
      headline = "",
      industryName = "",
      locationName = "",
      experience: rawExperience = [],
      skills: rawSkills = [],
      languages: rawLanguages = [],
    } = linkedInDataSnapshot;

    const experience = toArray(rawExperience);
    const skills = toArray(rawSkills);
    const languages = toArray(rawLanguages);

    const {
      desiredRole = "",
      selectedWorkStyles: rawSelectedWorkStyles = [],
      selectedMotivators: rawSelectedMotivators = [],
      workType = "",
      relocation = "",
    } = questionnaireData?.careerPreferences || {};

    const selectedWorkStyles = toArray(rawSelectedWorkStyles);
    const selectedMotivators = toArray(rawSelectedMotivators);

    const {
      ambition = "",
      ambitionStrategy = "",
      greatestSuccess = "",
      fitnessRituals = "",
    } = questionnaireData?.reflection || {};

    const fallbackContent = [
      "Autogenerated CV placeholder",
      "Reason: Candidate did not upload a CV file.",
      "",
      `LinkedIn URL: ${linkedInUrlToSend}`,
      `Name: ${firstName} ${lastName}`.trim(),
      `Headline: ${headline}`,
      `Industry: ${industryName}`,
      `Location: ${locationName}`,
      "",
      "Experience:",
      experience.length
        ? experience
            .map(
              (exp) =>
                `- ${exp.title || "Role"} at ${exp.company || "Company"} (${exp.start_date || "Start"} - ${exp.end_date || "Present"})`
            )
            .join("\n")
        : "- Not provided",
      "",
      `Skills: ${skills.length ? skills.join(", ") : "Not provided"}`,
      `Languages: ${
        languages.length
          ? languages.map((lang) => `${lang.name || "Language"} (${lang.proficiency || "Level"})`).join(", ")
          : "Not provided"
      }`,
      "",
      "Career Preferences:",
      `- Desired role: ${desiredRole || "Not provided"}`,
      `- Work styles: ${
        selectedWorkStyles.length ? selectedWorkStyles.join(", ") : "Not provided"
      }`,
      `- Motivators: ${
        selectedMotivators.length ? selectedMotivators.join(", ") : "Not provided"
      }`,
      `- Work type: ${workType || "Not provided"}`,
      `- Relocation: ${relocation || "Not provided"}`,
      "",
      "Reflection:",
      `- Ambition: ${ambition || "Not provided"}`,
      `- Ambition strategy: ${ambitionStrategy || "Not provided"}`,
      `- Greatest success: ${greatestSuccess || "Not provided"}`,
      `- Fitness rituals: ${fitnessRituals || "Not provided"}`,
      "",
      "Summaries supplied by the candidate:",
      preferencesSummaryText || "No career preferences summary provided.",
      "",
      reflectionSummaryText || "No reflection summary provided.",
    ].join("\n");

    return new File([fallbackContent], "autogenerated_cv.txt", {
      type: "text/plain",
    });
  };

  const processFiles = async () => {
    try {
      setProgress(5);

      const formData = new FormData();

      const linkedInDataToSend = normalizeLinkedInData(linkedInData);

      const linkedInUrlToSend =
        linkedInURL && linkedInURL.trim() !== ""
          ? linkedInURL
          : "https://www.linkedin.com/";

      const questionnaireSnapshot = {
        careerPreferences: {
          desiredRole: careerPreferences?.desiredRole || "",
          selectedWorkStyles: toArray(careerPreferences?.selectedWorkStyles),
          selectedMotivators: toArray(careerPreferences?.selectedMotivators),
          workType: careerPreferences?.workType || "",
          relocation: careerPreferences?.relocation || "",
        },
        reflection: {
          ambition: reflection?.ambition || "",
          ambitionStrategy: reflection?.ambitionStrategy || "",
          greatestSuccess: reflection?.greatestSuccess || "",
          fitnessRituals: reflection?.fitnessRituals || "",
        },
      };

      const cvFileToSend =
        cvFile ||
        buildFallbackCvFile(
          linkedInDataToSend,
          linkedInUrlToSend,
          preferencesSummary || "",
          reflectionSummary || "",
          questionnaireSnapshot
        );

      formData.append("cv_file", cvFileToSend);

      if (mLetterOption === "Upload File" && mLetterfile) {
        formData.append("cover_letter_file", mLetterfile);
      } else if (mLetterText) {
        formData.append("cover_letter_text", mLetterText);
      } else {
        formData.append("cover_letter_text", "");
      }

      formData.append("linkedin_data", JSON.stringify(linkedInDataToSend));
      formData.append("linkedin_url", linkedInUrlToSend);
      formData.append("preferences_summary", preferencesSummary || "");
      formData.append("reflection_summary", reflectionSummary || "");

      setProgress(20);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/analysis/process`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${Cookies.get("token")}`,
          },
          body: formData,
        }
      );

      setProgress(50);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Analysis failed");
      }

      const responseData = await response.json();
      const combinedResults = responseData.data;

      setProgress(80);

      try {
        await postAnalysisToAPI(
          cvFile,
          mLetterfile,
          linkedInURL,
          linkedInDataToSend,
          combinedResults,
          careerPreferences,
          reflection
        );
        console.log("Analysis successfully saved to API");
      } catch (error) {
        console.error("Failed to save analysis to API:", error);
      }

      // Update chart data with new structure (matching frontend useProcessFiles.js)
      if (combinedResults.bigFive) {
        const bigFiveScores = Object.entries(combinedResults.bigFive)
          .filter(
            ([trait]) =>
              !["workBehavior", "teamFitInsight", "careerGrowthTip"].includes(
                trait
              )
          )
          .map(([_, data]) => toFiniteNumber(data?.score, 0));

        setBigFiveData((prev) => ({
          ...prev,
          datasets: [
            {
              ...(prev?.datasets?.[0] || {}),
              data: bigFiveScores,
            },
          ],
        }));
      }

      if (combinedResults.disc) {
        const newDiscData = {
          labels: ["Dominance", "Influence", "Steadiness", "Conscientiousness"],
          datasets: [
            {
              data: [
                toFiniteNumber(combinedResults.disc.dominance?.score, 0),
                toFiniteNumber(combinedResults.disc.influence?.score, 0),
                toFiniteNumber(combinedResults.disc.steadiness?.score, 0),
                toFiniteNumber(combinedResults.disc.conscientiousness?.score, 0),
              ],
              backgroundColor: ["#FFD700", "#FFA500", "#FF8C00", "#EA650D"],
              borderColor: ["#ffffff", "#ffffff", "#ffffff", "#ffffff"],
              borderWidth: 2,
            },
          ],
        };
        setDiscData(newDiscData);
      }

      if (combinedResults.riasec) {
        const newRiasecData = {
          labels: [
            "Realistic",
            "Investigative",
            "Artistic",
            "Social",
            "Enterprising",
            "Conventional",
          ],
          datasets: [
            {
              label: "RIASEC Score",
              data: [
                toFiniteNumber(combinedResults.riasec.realistic?.score, 0),
                toFiniteNumber(combinedResults.riasec.investigative?.score, 0),
                toFiniteNumber(combinedResults.riasec.artistic?.score, 0),
                toFiniteNumber(combinedResults.riasec.social?.score, 0),
                toFiniteNumber(combinedResults.riasec.enterprising?.score, 0),
                toFiniteNumber(combinedResults.riasec.conventional?.score, 0),
              ],
              backgroundColor: "rgba(54, 162, 235, 0.2)",
              borderColor: "#A02F08",
              borderWidth: 2,
              pointBackgroundColor: [
                "#FFD700",
                "#FFA500",
                "#FF8C00",
                "#EA650D",
                "#E55100",
                "#D84315",
              ],
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: "#A02F08",
              pointRadius: 5,
            },
          ],
        };
        setRiasecData(newRiasecData);
      }

      if (combinedResults.mbti && combinedResults.mbti.dimensions) {
        const newMbtiData = {
          labels: [
            "Extraversion (E)",
            "Intuition (N)",
            "Thinking (T)",
            "Judging (J)",
          ],
          datasets: [
            {
              label: "MBTI Score",
              data: [
                toFiniteNumber(combinedResults.mbti.dimensions.extraversion_introversion?.score, 0),
                toFiniteNumber(combinedResults.mbti.dimensions.sensing_intuition?.score, 0),
                toFiniteNumber(combinedResults.mbti.dimensions.thinking_feeling?.score, 0),
                toFiniteNumber(combinedResults.mbti.dimensions.judging_perceiving?.score, 0),
              ],
              backgroundColor: "rgba(54, 162, 235, 0.2)",
              borderColor: "#A02F08",
              borderWidth: 2,
              pointBackgroundColor: [
                "#FFD700",
                "#FFA500",
                "#FF8C00",
                "#EA650D",
              ],
              pointBorderColor: "#fff",
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: "#A02F08",
              pointRadius: 5,
            },
          ],
        };
        setMbtiData(newMbtiData);
      }

      setAnalysisResults(combinedResults);
      setProgress(100);
      setAnalyzing(false);
    } catch (err) {
      console.error("Analysis Error:", err);
      setError(err.message);
      setAnalyzing(false);
    }
  };

  return {
    analyzing,
    setAnalyzing,
    progress,
    setProgress,
    analysisResults,
    setAnalysisResults,
    error,
    setError,
    processFiles,
  };
};

export default useProcessFilesBackend;